/**
 * GMO PG (mul-pay) Token SDK ローダー
 *
 * GMO PG の JavaScript Token SDK を使用して、
 * カードトークン化機能を提供します。
 *
 * 参考: https://docs.mul-pay.jp/payment/credit/tokenjs
 */

// GMO PG Token SDK の型定義
declare global {
  interface Window {
    Multipayment?: {
      init: (shopId: string) => void;
      getToken: (
        cardData: {
          cardno: string;
          expire: string;
          securitycode: string;
          holdername?: string;
        },
        callback: (response: { resultCode: string; tokenObject?: { token: string } }) => void
      ) => void;
    };
  }
}

let isLoaded = false;
let loadPromise: Promise<void> | null = null;
let loadedTestMode: boolean | null = null;

/**
 * GMO PG Token SDK をロード
 *
 * @param testMode テストモード（true: stg.static.mul-pay.jp、false: static.mul-pay.jp）
 */
const loadGmoPgSdk = async (testMode: boolean = false): Promise<void> => {
  // 既にロード済みで同じモードの場合はスキップ
  if (isLoaded && loadedTestMode === testMode) return;

  // 既にロード済みで異なるモードの場合は警告（再ロード不可）
  if (isLoaded && loadedTestMode !== testMode) {
    console.warn(
      `GMO PG SDK already loaded with ${loadedTestMode ? 'test' : 'production'} mode. ` +
      `Cannot switch to ${testMode ? 'test' : 'production'} mode without page reload.`
    );
    return;
  }

  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    // GMO PG の Token SDK スクリプトをロード
    const script = document.createElement('script');

    // testMode に応じて SDK URL を切り替え
    // テストモード: stg.static.mul-pay.jp（テスト用 Shop ID 対応）
    // 本番モード: static.mul-pay.jp（本番用 Shop ID のみ）
    script.src = testMode
      ? 'https://stg.static.mul-pay.jp/ext/js/token.js'
      : 'https://static.mul-pay.jp/ext/js/token.js';

    script.async = true;

    script.onload = () => {
      isLoaded = true;
      loadedTestMode = testMode;
      resolve();
    };

    script.onerror = () => {
      reject(new Error('GMO PG Token SDK のロードに失敗しました'));
    };

    document.head.appendChild(script);
  });

  return loadPromise;
};

/**
 * カード情報
 */
export interface CardData {
  cardNumber: string;
  expire: string; // YYMM 形式
  securityCode: string;
  holderName?: string;
}

/**
 * GMO PG のカードトークンを生成
 *
 * @param cardData カード情報
 * @param shopId Shop ID（API から取得、未指定時は環境変数を使用）
 * @param testMode テストモード（true: テスト環境 SDK を使用）
 * @returns トークン
 */
export const createCardToken = async (cardData: CardData, shopId?: string, testMode: boolean = false): Promise<string> => {
  await loadGmoPgSdk(testMode);

  // 優先順位: 引数 > 環境変数
  const resolvedShopId = shopId || import.meta.env.VITE_GMO_PG_SHOP_ID;

  if (!resolvedShopId) {
    console.error('GMO PG shop ID not configured!');
    throw new Error('GMO PG shop ID not configured');
  }

  if (!window.Multipayment) {
    throw new Error('GMO PG Token SDK が正しくロードされていません');
  }

  // SDK を初期化
  window.Multipayment.init(resolvedShopId);

  return new Promise((resolve, reject) => {
    window.Multipayment!.getToken(
      {
        cardno: cardData.cardNumber.replace(/\s/g, ''),
        expire: cardData.expire, // YYMM 形式
        securitycode: cardData.securityCode,
        holdername: cardData.holderName || '',
      },
      (response) => {
        if (response.resultCode === '000' && response.tokenObject?.token) {
          resolve(response.tokenObject.token);
        } else {
          // エラーコードに応じたメッセージ
          const errorMessage = getErrorMessage(response.resultCode);
          reject(new Error(errorMessage));
        }
      }
    );
  });
};

/**
 * GMO PG エラーコードからエラーメッセージを取得
 */
const getErrorMessage = (resultCode: string): string => {
  const errorMessages: Record<string, string> = {
    '100': 'カード番号が正しくありません',
    '101': '有効期限が正しくありません',
    '102': 'セキュリティコードが正しくありません',
    '110': 'カード番号が正しくありません（Luhnチェック失敗）',
    '111': '有効期限が正しくありません（期限切れ）',
    '112': '有効期限の形式が正しくありません',
    '113': 'セキュリティコードの形式が正しくありません',
    '121': 'カード名義人が正しくありません',
    '901': 'トークン取得に失敗しました',
    '902': 'リクエストタイムアウト',
    '999': '予期せぬエラーが発生しました',
  };

  return errorMessages[resultCode] || `トークン生成に失敗しました (エラーコード: ${resultCode})`;
};

// fincodeLoader と同じバリデーション関数をエクスポート
// （共通で使用するため）

/**
 * カード番号のバリデーション
 */
export const validateCardNumber = (cardNumber: string): boolean => {
  const cleaned = cardNumber.replace(/\s/g, '');

  // 長さチェック（13-19桁）
  if (!/^\d{13,19}$/.test(cleaned)) {
    return false;
  }

  // Luhn アルゴリズム
  let sum = 0;
  let isEven = false;

  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned[i], 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};

/**
 * カード番号をフォーマット（4桁ごとにスペース）
 */
export const formatCardNumber = (value: string): string => {
  const cleaned = value.replace(/\s/g, '');
  const groups = cleaned.match(/.{1,4}/g) || [];
  return groups.join(' ');
};

/**
 * 有効期限のバリデーション（MM/YY 形式）
 */
export const validateExpiry = (expiry: string): boolean => {
  const match = expiry.match(/^(\d{2})\/(\d{2})$/);
  if (!match) return false;

  const month = parseInt(match[1], 10);
  const year = parseInt('20' + match[2], 10);

  if (month < 1 || month > 12) return false;

  const now = new Date();
  const expiryDate = new Date(year, month - 1, 1);

  return expiryDate > now;
};

/**
 * 有効期限をフォーマット（MM/YY 形式）
 */
export const formatExpiry = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length >= 2) {
    return cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4);
  }
  return cleaned;
};

/**
 * 有効期限を YYMM 形式に変換（GMO PG API 用）
 */
export const expiryToYYMM = (expiry: string): string => {
  const match = expiry.match(/^(\d{2})\/(\d{2})$/);
  if (!match) throw new Error('Invalid expiry format');

  return match[2] + match[1]; // YYMM
};

/**
 * セキュリティコードのバリデーション
 */
export const validateSecurityCode = (code: string): boolean => {
  return /^\d{3,4}$/.test(code);
};

/**
 * カードブランドを判定
 */
export const getCardBrand = (cardNumber: string): string | null => {
  const cleaned = cardNumber.replace(/\s/g, '');

  if (/^4/.test(cleaned)) return 'Visa';
  if (/^5[1-5]/.test(cleaned)) return 'MasterCard';
  if (/^3[47]/.test(cleaned)) return 'American Express';
  if (/^35/.test(cleaned)) return 'JCB';
  if (/^6(?:011|5)/.test(cleaned)) return 'Discover';

  return null;
};
